import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/feature/AppShell";
import ProspectCard, { type Prospect } from "@/pages/prospectar/components/ProspectCard";
import OpenAndWritePanel, { copyText, type ComposerState } from "./components/OpenAndWritePanel";
import ProspectModal from "@/pages/prospectar/components/ProspectModal";
import ConversationPanel from "./components/ConversationPanel";
import SendQueuePanel from "./components/SendQueuePanel";
import { buildDefaultOpener } from "@/data/outreach";
import { addToQueue, fetchQueueIds } from "@/services/sendQueue";
import ConnectedAccounts from "@/pages/prospectar/components/ConnectedAccounts";
import { COUNTRIES, findCountry, parseRegion } from "@/data/regions";
import { nicheOptions, platformOptions, statusOptions, scoreOptions } from "@/mocks/prospectos";
import { FOLLOWER_RANGES, SEARCH_LANGUAGES, languagesFor, matchesFollowerRange, type SearchLanguage } from "@/data/searchFilters";

const followerOptions = ["Todos", ...FOLLOWER_RANGES.slice(1).map((r) => r.label)];
import {
  fetchProspectsWithOpenerState,
  saveOpener,
  approveOpener,
  markOpenerSent,
  searchProspects,
  searchProfilesByNetwork,
  scoreProspects,
  type ProfileSearchGeo,
} from "@/services/prospects";

type SelectProps = {
  label: string;
  icon: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
};

function FilterSelect({ label, icon, value, options, onChange }: SelectProps) {
  return (
    <label className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-xl px-3 py-2 min-w-0">
      <i className={`${icon} text-foreground-400 text-sm`}></i>
      <span className="text-[11px] text-foreground-500 whitespace-nowrap hidden lg:inline">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent outline-none text-sm text-foreground-950 cursor-pointer max-w-[120px] truncate"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <i className="ri-arrow-down-s-line text-foreground-400 pointer-events-none"></i>
    </label>
  );
}

function scoreMatch(score: number, filter: string) {
  if (filter === "Todos") return true;
  if (filter === "90+") return score >= 90;
  if (filter === "80 - 89") return score >= 80 && score < 90;
  if (filter === "70 - 79") return score >= 70 && score < 80;
  if (filter === "Menos de 70") return score < 70;
  return true;
}

function followerMatch(f: number, filter: string) {
  return matchesFollowerRange(f, filter);
}

// Red social en la que se busca al presionar el botón de búsqueda en vivo.
// Por ahora solo Instagram está conectado del lado del backend (Apify);
// Facebook devuelve páginas públicas (sin bio ni seguidores).
const SEARCH_NETWORKS: { value: "instagram" | "facebook" | "tiktok"; label: string; icon: string; enabled: boolean }[] = [
  { value: "instagram", label: "Instagram", icon: "ri-instagram-line", enabled: true },
  { value: "facebook", label: "Facebook", icon: "ri-facebook-circle-line", enabled: true },
  { value: "tiktok", label: "TikTok", icon: "ri-tiktok-line", enabled: true },
];

// Listas de referencia rápida (chips) para completar los campos de búsqueda
// con un toque, sin perder la posibilidad de escribir libremente.
const HASHTAG_SUGGESTIONS = [
  "coachingonline",
  "desarrollopersonal",
  "fitness",
  "finanzaspersonales",
  "marketingdigital",
  "tecnologia",
  "moda",
  "gastronomia",
];


// Máximo de categorías sugeridas que se pueden combinar en una sola búsqueda.
const MAX_CATEGORIES = 3;

export default function Prospectar() {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [regionEntry, setRegionEntry] = useState("");
  const [city, setCity] = useState("");
  const [strictGeo, setStrictGeo] = useState(false);
  // Rango de seguidores y idioma de la búsqueda (por defecto: español).
  const [followerRange, setFollowerRange] = useState(0);
  const [language, setLanguage] = useState<SearchLanguage>("es");
  // Categorías sugeridas elegidas con un toque (máximo MAX_CATEGORIES),
  // independientes del hashtag escrito a mano en `query`. Aparecen como
  // chips al comienzo del campo de búsqueda, antes del texto escrito.
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [score, setScore] = useState("Todos");
  const [followers, setFollowers] = useState("Todos");
  const [niche, setNiche] = useState("Todos");
  const [platform, setPlatform] = useState("Todas");
  const [status, setStatus] = useState("Todos");

  const [searchNetwork, setSearchNetwork] = useState<"instagram" | "facebook" | "tiktok">("instagram");
  const [searchMode, setSearchMode] = useState<"perfiles" | "hashtags">("perfiles");
  const [searching, setSearching] = useState(false);

  const [selected, setSelected] = useState<Prospect | null>(null);
  const [openers, setOpeners] = useState<Record<string, string>>({});
  const [approved, setApproved] = useState<Record<string, boolean>>({});
  const [sent, setSent] = useState<Record<string, boolean>>({});
  // Un prospecto está "busy" mientras su Generar/Aprobar/Enviar está escribiendo
  // en Supabase — evita doble clic y escrituras/eventos duplicados.
  const [busyIds, setBusyIds] = useState<Record<string, boolean>>({});
  const [generating, setGenerating] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Flujo "Abrir y escribir": panel con el mensaje copiado mientras la persona lo envía en la red.
  const [composer, setComposer] = useState<ComposerState | null>(null);
  const [composerBusy, setComposerBusy] = useState(false);
  const prepRef = useRef<Record<string, Promise<void>>>({});
  // Conversación (respuestas) abierta y vista "con actividad" (respondieron / abrieron el link).
  const [convId, setConvId] = useState<string | null>(null);
  const [inbox, setInbox] = useState(false);
  // Cola de envío: ids en cola y panel abierto.
  const [queueIds, setQueueIds] = useState<string[]>([]);
  const [queueOpen, setQueueOpen] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2600);
  };

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(cat)) return prev.filter((c) => c !== cat);
      if (prev.length >= MAX_CATEGORIES) {
        showToast(`Puedes elegir máximo ${MAX_CATEGORIES} categorías`);
        return prev;
      }
      return [...prev, cat];
    });
  };

  const removeCategory = (cat: string) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== cat));
  };

  const loadProspects = useCallback(async () => {
    const { prospects: data, openerState } = await fetchProspectsWithOpenerState();
    setProspects(data);
    const openersMap: Record<string, string> = {};
    const approvedMap: Record<string, boolean> = {};
    const sentMap: Record<string, boolean> = {};
    for (const [id, state] of Object.entries(openerState)) {
      if (state.opener) openersMap[id] = state.opener;
      approvedMap[id] = state.approved;
      sentMap[id] = state.sent;
    }
    setOpeners(openersMap);
    setApproved(approvedMap);
    setSent(sentMap);
  }, []);

  /** Calcula el score de los prospectos nuevos y recarga la lista. */
  const scoreAndLoad = useCallback(async () => {
    try {
      await scoreProspects();
    } catch (err) {
      console.error("score-prospects", err);
    }
    await loadProspects();
  }, [loadProspects]);

  useEffect(() => {
    let active = true;
    scoreAndLoad()
      .catch((err) => {
        if (active) setLoadError(err instanceof Error ? err.message : "No se pudo cargar la lista de prospectos.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [scoreAndLoad]);

  useEffect(() => {
    fetchQueueIds().then(setQueueIds).catch(() => undefined);
  }, []);

  const setBusy = (id: string, value: boolean) => {
    setBusyIds((prev) => ({ ...prev, [id]: value }));
  };

  const patchProspect = (id: string, patch: Partial<Prospect>) => {
    setProspects((prev) => prev.map((pr) => (pr.id === id ? { ...pr, ...patch } : pr)));
    setSelected((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
  };

  const filtered = useMemo(() => {
    const activityAt = (p: Prospect) => Math.max(p.lastReplyAt ? Date.parse(p.lastReplyAt) : 0, p.linkLastClickAt ? Date.parse(p.linkLastClickAt) : 0);
    return prospects.filter((p) => {
      if (inbox && !(p.replyCount || p.linkClicks || p.linkRegistered)) return false;
      if (!scoreMatch(p.score, score)) return false;
      if (!followerMatch(p.followers, followers)) return false;
      if (niche !== "Todos" && p.niche !== niche) return false;
      if (platform !== "Todas" && p.platform !== platform) return false;
      if (status !== "Todos" && p.status !== status) return false;
      return true;
    }).sort((a, b) => (inbox ? activityAt(b) - activityAt(a) : 0));
  }, [prospects, score, followers, niche, platform, status, inbox]);

  const activeCount = useMemo(() => prospects.filter((p) => p.replyCount || p.linkClicks || p.linkRegistered).length, [prospects]);
  const convProspect = convId ? prospects.find((p) => p.id === convId) ?? null : null;

  /** Opener por defecto: saludo con el nombre, su tema y una pregunta abierta (sin promesas de ingresos). */
  const buildOpenerText = (p: Prospect) => buildDefaultOpener({ name: p.name, niche: p.niche, platform: p.platform });

  const generateOpener = (p: Prospect) => {
    if (busyIds[p.id]) return;
    setBusy(p.id, true);
    setGenerating(true);
    window.setTimeout(async () => {
      const text = buildOpenerText(p);
      try {
        const { nextAction } = await saveOpener(p.id, text);
        setOpeners((prev) => ({ ...prev, [p.id]: text }));
        patchProspect(p.id, { nextAction });
        showToast(`Opener generado y guardado para ${p.name}`);
      } catch (err) {
        showToast(err instanceof Error ? `No se pudo guardar el opener: ${err.message}` : "No se pudo guardar el opener.");
      } finally {
        setGenerating(false);
        setBusy(p.id, false);
      }
    }, 1000);
  };

  const approve = async (p: Prospect) => {
    if (busyIds[p.id]) return;
    if (!openers[p.id]) {
      showToast("Primero genera el opener con IA");
      return;
    }
    setBusy(p.id, true);
    try {
      const { nextAction } = await approveOpener(p.id);
      setApproved((prev) => ({ ...prev, [p.id]: true }));
      patchProspect(p.id, { nextAction });
      showToast(`Opener aprobado para ${p.name}`);
    } catch (err) {
      showToast(err instanceof Error ? `No se pudo aprobar: ${err.message}` : "No se pudo aprobar.");
    } finally {
      setBusy(p.id, false);
    }
  };

  const send = async (p: Prospect) => {
    if (busyIds[p.id]) return;
    if (!approved[p.id]) {
      showToast("Aprueba el opener antes de enviar");
      return;
    }
    setBusy(p.id, true);
    try {
      const { pipelineStage, nextAction, followUp } = await markOpenerSent(p.id, p.status);
      setSent((prev) => ({ ...prev, [p.id]: true }));
      patchProspect(p.id, { status: pipelineStage, nextAction, followUp });
      showToast(`Mensaje marcado como enviado a ${p.name}`);
    } catch (err) {
      showToast(err instanceof Error ? `No se pudo guardar el envío: ${err.message}` : "No se pudo guardar el envío.");
    } finally {
      setBusy(p.id, false);
    }
  };

  /** Abre el perfil en otra pestaña y copia el mensaje; guarda y aprueba el opener en segundo plano. */
  const openAndWrite = (p: Prospect) => {
    if (!p.profileUrl) {
      showToast("Este prospecto no tiene enlace de perfil");
      return;
    }
    const existing = openers[p.id];
    const text = existing ?? buildOpenerText(p);
    // Ambas acciones ocurren dentro del toque del usuario para que el navegador no bloquee la pestaña ni el copiado.
    const copyPromise = copyText(text);
    window.open(p.profileUrl, "_blank", "noopener,noreferrer");
    setComposer({ prospect: p, text, copied: true });
    void copyPromise.then((ok) => setComposer((c) => (c && c.prospect.id === p.id ? { ...c, copied: ok } : c)));

    prepRef.current[p.id] = (async () => {
      try {
        if (!existing) {
          const { nextAction } = await saveOpener(p.id, text);
          setOpeners((prev) => ({ ...prev, [p.id]: text }));
          patchProspect(p.id, { nextAction });
        }
        if (!approved[p.id]) {
          const { nextAction } = await approveOpener(p.id);
          setApproved((prev) => ({ ...prev, [p.id]: true }));
          patchProspect(p.id, { nextAction });
        }
      } catch (err) {
        showToast(err instanceof Error ? `No se pudo guardar el mensaje: ${err.message}` : "No se pudo guardar el mensaje.");
      }
    })();
  };

  /** Guarda el texto (si cambió) y aprueba el opener; lo usa la cola de envío. */
  const prepareOpener = async (p: Prospect, text: string) => {
    if (openers[p.id] !== text) {
      const { nextAction } = await saveOpener(p.id, text);
      setOpeners((prev) => ({ ...prev, [p.id]: text }));
      patchProspect(p.id, { nextAction });
    }
    if (!approved[p.id]) {
      const { nextAction } = await approveOpener(p.id);
      setApproved((prev) => ({ ...prev, [p.id]: true }));
      patchProspect(p.id, { nextAction });
    }
  };

  const markSentFor = async (p: Prospect) => {
    const current = prospects.find((x) => x.id === p.id) ?? p;
    const { pipelineStage, nextAction, followUp } = await markOpenerSent(p.id, current.status);
    setSent((prev) => ({ ...prev, [p.id]: true }));
    patchProspect(p.id, { status: pipelineStage, nextAction, followUp });
  };

  const queueOne = async (p: Prospect) => {
    if (queueIds.includes(p.id)) return;
    try {
      await addToQueue([p.id]);
      setQueueIds((prev) => [...prev, p.id]);
      showToast(`${p.name} quedó en la cola de envío`);
    } catch (err) {
      showToast(err instanceof Error ? `No se pudo agregar: ${err.message}` : "No se pudo agregar a la cola.");
    }
  };

  const markComposerSent = async () => {
    if (!composer || composerBusy) return;
    const p = composer.prospect;
    setComposerBusy(true);
    try {
      await prepRef.current[p.id];
      const current = prospects.find((x) => x.id === p.id) ?? p;
      const { pipelineStage, nextAction, followUp } = await markOpenerSent(p.id, current.status);
      setSent((prev) => ({ ...prev, [p.id]: true }));
      patchProspect(p.id, { status: pipelineStage, nextAction, followUp });
      showToast(`Mensaje marcado como enviado a ${p.name}`);
      setComposer(null);
    } catch (err) {
      showToast(err instanceof Error ? `No se pudo guardar el envío: ${err.message}` : "No se pudo guardar el envío.");
    } finally {
      setComposerBusy(false);
    }
  };

  const country = countryCode ? findCountry(countryCode) : undefined;
  const region = country && regionEntry ? parseRegion(regionEntry) : undefined;

  /** Ubicación elegida (país → estado/provincia → ciudad) para guiar la búsqueda. */
  const buildGeo = (): ProfileSearchGeo | undefined => {
    if (!country) return undefined;
    const cityText = city.trim();
    return {
      country_code: country.code,
      country_name: country.name,
      region: region?.name.replace(/\s*\(.*\)\s*$/, "") ?? "",
      city: cityText,
      place: cityText || region?.search || country.name,
      terms: [...(region?.terms ?? []), ...(cityText ? [cityText] : [])],
      strict: strictGeo,
    };
  };

  const runProfileSearch = async () => {
    if (searching) return;
    const base = query.trim() || selectedCategories[0] || "";
    if (base.length < 2) {
      showToast("Escribe un nombre, usuario o palabra clave para buscar perfiles");
      return;
    }
    const geo = buildGeo();
    setSearching(true);
    try {
      const range = FOLLOWER_RANGES[followerRange] ?? FOLLOWER_RANGES[0];
      const r = await searchProfilesByNetwork(searchNetwork, base, 20, geo, undefined, {
        min_followers: range.min || undefined,
        max_followers: range.max || undefined,
        languages: languagesFor(language),
      });
      await scoreAndLoad();
      if (r.inserted > 0) {
        setScore("Todos");
        setFollowers("Todos");
        setNiche("Todos");
        setPlatform("Todas");
        setStatus("Todos");
        showToast(`Encontramos ${r.inserted} perfil${r.inserted === 1 ? "" : "es"} nuevo${r.inserted === 1 ? "" : "s"} para "${base}"${geo ? ` en ${geo.place}${r.geo_confirmed ? ` (${r.geo_confirmed} con ubicación confirmada)` : ""}` : ""}${r.private ? ` · ${r.private} privado${r.private === 1 ? "" : "s"} omitido${r.private === 1 ? "" : "s"}` : ""}`);
      } else if (r.message === "all_duplicates") {
        showToast("Esos perfiles ya estaban en tu lista de prospectos");
      } else {
        const hint = r.out_of_range ? ` ${r.out_of_range} quedaron fuera del rango de seguidores.` : r.lang_filtered ? ` ${r.lang_filtered} estaban en otro idioma.` : "";
        showToast(`No encontramos perfiles públicos nuevos para "${base}".${hint} Prueba con otra palabra o amplía el rango.`);
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : "No se pudo completar la búsqueda.");
    } finally {
      setSearching(false);
    }
  };

  const runLiveSearch = async () => {
    if (searchMode === "perfiles" || searchNetwork === "facebook") return runProfileSearch();
    if (searching) return;
    const q = query.trim();
    if (!q && selectedCategories.length === 0) {
      showToast("Escribe un hashtag o elige al menos una categoría sugerida");
      return;
    }
    const loc = city.trim() || region?.search || "";
    setSearching(true);
    try {
      const result = await searchProspects(searchNetwork, q, { location: loc || undefined, categories: selectedCategories, languages: languagesFor(language) });
      await scoreAndLoad();
      const terms = result.categories?.length ? result.categories.map((c) => `#${c}`).join(", ") : q;
      if (result.inserted > 0) {
        const where = loc ? ` cerca de ${loc}` : "";
        showToast(`Encontramos ${result.inserted} prospecto${result.inserted === 1 ? "" : "s"} nuevo${result.inserted === 1 ? "" : "s"} en ${terms}${where}`);
      } else if (result.message === "no_location_matches") {
        showToast(`No encontramos perfiles de ${terms} cerca de ${result.location ?? loc}. Prueba sin ubicación o con otra ciudad.`);
      } else if (result.message === "all_duplicates") {
        showToast("Esos perfiles ya estaban en tu lista de prospectos");
      } else {
        showToast("No encontramos perfiles nuevos con esos hashtags");
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : "No se pudo completar la búsqueda.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <AppShell headerVariant="onDark">
      <section className="bg-background-950 text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-2 pb-8 relative overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 opacity-50"
            style={{ background: "radial-gradient(600px 240px at 85% 0%, oklch(var(--accent-700) / 0.5), transparent 60%)" }}
          ></div>
          <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold text-primary-300">
                <i className="ri-radar-line"></i> Prospección Inteligente
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-extrabold mt-2">Prospectar</h1>
              <p className="text-foreground-300 text-sm mt-1 max-w-xl">
                Descubre perfiles ideales para tu marca, analizados por IA y listos para contactar.
              </p>
            </div>
            <div className="flex items-center gap-3 text-center">
              <div className="bg-background-900/70 border border-accent-700/40 rounded-2xl px-4 py-3">
                <div className="font-heading font-extrabold text-2xl text-primary-400">{prospects.length}</div>
                <div className="text-[11px] text-foreground-300">Recomendados</div>
              </div>
              <div className="bg-background-900/70 border border-accent-700/40 rounded-2xl px-4 py-3">
                <div className="font-heading font-extrabold text-2xl text-accent-300">5K–50K</div>
                <div className="text-[11px] text-foreground-300">Rango ideal</div>
              </div>
            </div>
          </div>

          {/* Modo de búsqueda */}
          <div className="relative mt-6 inline-flex rounded-full bg-background-900/70 border border-accent-800/50 p-1" role="tablist" aria-label="Tipo de búsqueda">
            {([
              ["perfiles", "Perfiles", "ri-user-search-line"],
              ["hashtags", "Hashtags", "ri-hashtag"],
            ] as const).map(([k, label, icon]) => (
              <button
                key={k}
                role="tab"
                aria-selected={searchMode === k}
                onClick={() => setSearchMode(k)}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors ${searchMode === k ? "bg-primary-500 text-background-950" : "text-foreground-300 hover:text-primary-300"}`}
              >
                <i className={icon}></i>
                {label}
              </button>
            ))}
          </div>

          {/* Search bar */}
          <div className="relative mt-3 flex flex-col sm:flex-row gap-2">
            {/* Campo de hashtag: las categorías elegidas aparecen como chips al
                comienzo, antes del texto escrito a mano. */}
            <div className="flex-1 flex flex-wrap items-center gap-2 bg-background-900/70 border border-accent-800/50 rounded-full px-4 py-2.5 min-h-[46px]">
              <i className="ri-search-line text-foreground-300"></i>
              {selectedCategories.map((cat) => (
                <span
                  key={cat}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold bg-primary-500 text-background-950 pl-2.5 pr-1.5 py-1 rounded-full whitespace-nowrap"
                >
                  #{cat}
                  <span
                    role="button"
                    aria-label={`Quitar ${cat}`}
                    onClick={() => removeCategory(cat)}
                    className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-background-950/20 hover:bg-background-950/40 cursor-pointer"
                  >
                    <i className="ri-close-line text-[11px] leading-none"></i>
                  </span>
                </span>
              ))}
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                type="text"
                placeholder={
                  searchMode === "perfiles"
                    ? "Nombre, usuario o palabra clave (ej: coach de vida)…"
                    : selectedCategories.length > 0
                      ? "Agrega otro hashtag (opcional)…"
                      : "Hashtag o palabra clave para buscar (ej: coachingonline)…"
                }
                className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-foreground-100 placeholder:text-foreground-400"
              />
            </div>
            <div className="flex items-center gap-1 bg-background-900/70 border border-accent-800/50 rounded-full px-1.5 py-1.5">
              {SEARCH_NETWORKS.map((n) => (
                <button
                  key={n.value}
                  type="button"
                  disabled={!n.enabled}
                  title={n.enabled ? n.label : `${n.label} — próximamente`}
                  onClick={() => setSearchNetwork(n.value)}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-base transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-30 ${
                    searchNetwork === n.value && n.enabled
                      ? "bg-primary-500 text-background-950"
                      : "text-foreground-300 hover:bg-background-800/60"
                  }`}
                >
                  <i className={n.icon}></i>
                </button>
              ))}
            </div>
            <button
              onClick={runLiveSearch}
              disabled={searching}
              className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-950 rounded-full px-5 py-3 font-heading font-bold text-sm hover:bg-primary-400 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
            >
              {searching ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> Buscando…
                </>
              ) : (
                <>
                  <i className="ri-sparkling-2-fill"></i> Buscar prospectos
                </>
              )}
            </button>
          </div>

          {/* Ubicación: país → estado / provincia → ciudad */}
          <div className="relative mt-2 flex flex-col sm:flex-row gap-2">
            <label className="flex-1 flex items-center gap-2 bg-background-900/70 border border-accent-800/50 rounded-full px-4 py-2.5">
              <i className="ri-earth-line text-foreground-300"></i>
              <select
                value={countryCode}
                onChange={(e) => {
                  const code = e.target.value;
                  setCountryCode(code);
                  setRegionEntry("");
                  // Brasil habla portugués; al salir de Brasil se vuelve al español.
                  if (code === "BR" && language === "es") setLanguage("pt");
                  else if (code !== "BR" && language === "pt") setLanguage("es");
                }}
                aria-label="País"
                className="w-full bg-transparent outline-none text-sm text-foreground-100 cursor-pointer"
              >
                <option value="" className="text-slate-900">Todos los países</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code} className="text-slate-900">
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={`flex-1 flex items-center gap-2 bg-background-900/70 border border-accent-800/50 rounded-full px-4 py-2.5 ${country ? "" : "opacity-50"}`}>
              <i className="ri-map-pin-line text-foreground-300"></i>
              <select
                value={regionEntry}
                onChange={(e) => setRegionEntry(e.target.value)}
                disabled={!country}
                aria-label={country?.divisionLabel ?? "Estado o provincia"}
                className="w-full bg-transparent outline-none text-sm text-foreground-100 cursor-pointer disabled:cursor-not-allowed"
              >
                <option value="" className="text-slate-900">
                  {country ? `Todo el país · ${country.divisionLabel}…` : "Elige primero un país"}
                </option>
                {country?.regions.map((r) => (
                  <option key={r} value={r} className="text-slate-900">
                    {parseRegion(r).name}
                  </option>
                ))}
              </select>
            </label>
            <label className={`flex-1 flex items-center gap-2 bg-background-900/70 border border-accent-800/50 rounded-full px-4 py-2.5 ${country ? "" : "opacity-50"}`}>
              <i className="ri-building-line text-foreground-300"></i>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={!country}
                type="text"
                maxLength={40}
                placeholder="Ciudad (opcional)"
                className="w-full bg-transparent outline-none text-sm text-foreground-100 placeholder:text-foreground-400 disabled:cursor-not-allowed"
              />
            </label>
          </div>
          {/* Filtros de la búsqueda: cantidad de seguidores e idioma */}
          <div className="relative mt-3 flex flex-col sm:flex-row gap-3">
            <label className="flex-1 flex items-center gap-2 bg-background-900/70 border border-accent-800/50 rounded-full px-4 py-2.5">
              <i className="ri-group-line text-foreground-300"></i>
              <span className="text-[11px] text-foreground-400 whitespace-nowrap">Seguidores</span>
              <select
                value={followerRange}
                onChange={(e) => setFollowerRange(Number(e.target.value))}
                aria-label="Cantidad de seguidores"
                className="w-full bg-transparent outline-none text-sm text-foreground-100 cursor-pointer"
              >
                {FOLLOWER_RANGES.map((r, i) => (
                  <option key={r.label} value={i} className="text-slate-900">
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex-1 flex items-center gap-2 bg-background-900/70 border border-accent-800/50 rounded-full px-4 py-2.5">
              <i className="ri-translate-2 text-foreground-300"></i>
              <span className="text-[11px] text-foreground-400 whitespace-nowrap">Idioma</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as SearchLanguage)}
                aria-label="Idioma de los perfiles y publicaciones"
                className="w-full bg-transparent outline-none text-sm text-foreground-100 cursor-pointer"
              >
                {SEARCH_LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value} className="text-slate-900">
                    {l.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {country ? (
            <div className="relative mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              {searchMode === "perfiles" ? (
                <label className="inline-flex items-center gap-2 text-[11px] text-foreground-300 cursor-pointer">
                  <input type="checkbox" checked={strictGeo} onChange={(e) => setStrictGeo(e.target.checked)} className="accent-primary-500" />
                  Solo perfiles que mencionan esta ubicación en su bio
                </label>
              ) : null}
              {country.hint ? <span className="text-[11px] text-foreground-400">{country.hint}</span> : null}
            </div>
          ) : null}

          {/* Listas de referencia tocables — complementan los campos de arriba, no los reemplazan.
              Las categorías son de selección múltiple (hasta MAX_CATEGORIES); al elegirlas aparecen
              como chips al comienzo del campo de hashtag (arriba) y se pueden quitar desde ahí o
              desde aquí, con la X que aparece en cada chip elegido. La ciudad sigue siendo una sola. */}
          <div className="relative mt-3 flex flex-col sm:flex-row gap-x-6 gap-y-2">
            <div className="flex-1 flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-foreground-400 whitespace-nowrap">
                Categorías (máx. {MAX_CATEGORIES}):
              </span>
              {HASHTAG_SUGGESTIONS.map((h) => {
                const isSelected = selectedCategories.includes(h);
                const isDisabled = !isSelected && selectedCategories.length >= MAX_CATEGORIES;
                return (
                  <button
                    key={h}
                    type="button"
                    disabled={isDisabled}
                    title={isDisabled ? `Ya elegiste ${MAX_CATEGORIES} categorías — quita una para cambiarla` : undefined}
                    onClick={() => toggleCategory(h)}
                    className={`inline-flex items-center gap-1 text-[11px] pl-2.5 pr-1.5 py-1 rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
                      isSelected
                        ? "bg-primary-500 text-background-950 border-primary-500 pr-1.5"
                        : isDisabled
                        ? "bg-background-900/40 border-background-800/40 text-foreground-600 opacity-40 cursor-not-allowed pr-2.5"
                        : "bg-background-900/70 border-accent-800/50 text-foreground-300 hover:text-primary-300 hover:border-primary-500/50 pr-2.5"
                    }`}
                  >
                    #{h}
                    {isSelected ? (
                      <span
                        role="button"
                        aria-label={`Quitar ${h}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeCategory(h);
                        }}
                        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-background-950/20 hover:bg-background-950/40"
                      >
                        <i className="ri-close-line text-[11px] leading-none"></i>
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="relative mt-2 text-[11px] text-foreground-400">
            {searchNetwork === "facebook"
              ? "Facebook: escribe un nombre, tema o palabra clave y trae páginas públicas relacionadas (nombre y enlace; no incluye bio ni seguidores). Los mensajes llegan al buzón de la página. Prueba varias palabras para ampliar. "
              : searchMode === "perfiles"
                ? `Busca perfiles públicos de ${SEARCH_NETWORKS.find((n) => n.value === searchNetwork)?.label} como en su buscador (por nombre, usuario o palabra clave), traiga o no interacción con tu contenido, y los guarda con su bio y seguidores. Prueba varias palabras para ampliar.`
                : `Busca en ${SEARCH_NETWORKS.find((n) => n.value === searchNetwork)?.label} por hashtag, hasta ${MAX_CATEGORIES} categorías y, opcionalmente, por ubicación, y guarda los perfiles nuevos directo en tu lista.`}          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
          <ConnectedAccounts onProspects={() => void scoreAndLoad()} notify={showToast} />

          {/* AI banner */}
          <div className="rounded-2xl bg-secondary-500/10 border border-secondary-500/25 p-4 flex items-start gap-3">
            <span className="w-9 h-9 rounded-xl bg-secondary-500/20 text-secondary-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-robot-2-line"></i>
            </span>
            <p className="text-sm text-foreground-800">
              La IA prioriza perfiles de <span className="font-semibold text-foreground-950">5.000 a 50.000 seguidores</span> con
              audiencias afines a tu perfil <span className="font-semibold vv-gradient-text">Estratega Analítico</span>. A mayor Score, mejor
              oportunidad de conversión.
            </p>
          </div>

          {/* Filters */}
          <div className="mt-4 flex flex-wrap gap-2">
            <FilterSelect label="Score" icon="ri-bar-chart-2-line" value={score} options={scoreOptions} onChange={setScore} />
            <FilterSelect label="Seguidores" icon="ri-group-line" value={followers} options={followerOptions} onChange={setFollowers} />
            <FilterSelect label="Nicho" icon="ri-price-tag-3-line" value={niche} options={nicheOptions} onChange={setNiche} />
            <FilterSelect label="Plataforma" icon="ri-share-circle-line" value={platform} options={platformOptions} onChange={setPlatform} />
            <FilterSelect label="Estado" icon="ri-flag-2-line" value={status} options={statusOptions} onChange={setStatus} />
          </div>

          {/* Results */}
          <div className="mt-5 flex items-center justify-between">
            <h2 className="font-heading font-bold text-foreground-950">
              Prospectos recomendados
              <span className="text-foreground-500 font-normal text-sm ml-2">({filtered.length})</span>
            </h2>
            <button
              onClick={() => setInbox((v) => !v)}
              className={`ml-auto mr-3 px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap border ${
                inbox ? "bg-primary-500 text-background-950 border-primary-500" : "bg-background-100 text-foreground-700 border-background-300 hover:border-primary-400"
              }`}
            >
              <i className="ri-chat-3-line"></i> Con actividad ({activeCount})
            </button>
            <button
              onClick={() => setQueueOpen(true)}
              className="mr-3 px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap border bg-background-100 text-foreground-700 border-background-300 hover:border-primary-400"
            >
              <i className="ri-play-list-add-line"></i> Cola de envío ({queueIds.length})
            </button>
            <button
              onClick={() => {
                setScore("Todos");
                setFollowers("Todos");
                setNiche("Todos");
                setPlatform("Todas");
                setStatus("Todos");
                setQuery("");
                setFollowerRange(0);
                setCountryCode("");
                setRegionEntry("");
                setCity("");
                setSelectedCategories([]);
              }}
              className="text-sm text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i> Limpiar filtros
            </button>
          </div>

          {loading ? (
            <div className="mt-6 bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-2xl"></i>
              <p className="mt-2">Cargando prospectos desde Supabase…</p>
            </div>
          ) : loadError ? (
            <div className="mt-6 bg-background-100 border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700">
              {loadError}
            </div>
          ) : filtered.length === 0 ? (
            <div className="mt-6 bg-background-100 border border-dashed border-background-300 rounded-2xl p-10 text-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
              <p className="text-foreground-600 mt-3">No encontramos prospectos con esos filtros.</p>
              <p className="text-sm text-foreground-500">Prueba ampliar el rango de Score o de seguidores, o busca un hashtag nuevo arriba.</p>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filtered.map((p) => (
                <ProspectCard
                  key={p.id}
                  prospect={p}
                  opener={openers[p.id] ?? null}
                  approved={!!approved[p.id]}
                  sent={!!sent[p.id]}
                  busy={!!busyIds[p.id]}
                  onView={(pr) => setSelected(pr)}
                  onGenerate={generateOpener}
                  onApprove={approve}
                  onSend={send}
                  onOpenWrite={openAndWrite}
                  onReply={(pr) => setConvId(pr.id)}
                  onQueue={(pr) => void queueOne(pr)}
                  queued={queueIds.includes(p.id)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <ProspectModal
        prospect={selected}
        opener={selected ? openers[selected.id] ?? null : null}
        approved={selected ? !!approved[selected.id] : false}
        sent={selected ? !!sent[selected.id] : false}
        generating={selected ? generating && !!busyIds[selected.id] : false}
        onClose={() => setSelected(null)}
        onGenerate={generateOpener}
        onApprove={approve}
        onSend={send}
      />
      {queueOpen ? (
        <SendQueuePanel
          prospects={prospects}
          openers={openers}
          sent={sent}
          queueIds={queueIds}
          buildText={buildOpenerText}
          prepare={prepareOpener}
          markSent={markSentFor}
          onQueueChange={setQueueIds}
          onClose={() => setQueueOpen(false)}
          onToast={showToast}
        />
      ) : null}
      {convProspect ? (
        <ConversationPanel
          key={convProspect.id}
          prospect={convProspect}
          onClose={() => setConvId(null)}
          onPatch={(id, patch) => patchProspect(id, patch)}
          onToast={showToast}
        />
      ) : null}
      {composer ? (
        <OpenAndWritePanel
          state={composer}
          sent={!!sent[composer.prospect.id]}
          busy={composerBusy}
          onClose={() => setComposer(null)}
          onReopen={() => window.open(composer.prospect.profileUrl, "_blank", "noopener,noreferrer")}
          onMarkSent={() => void markComposerSent()}
          onCopied={(ok) => showToast(ok ? "Mensaje copiado" : "No se pudo copiar. Selecciona el texto y cópialo.")}
        />
      ) : null}


      {toast ? (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[60] bg-background-950 text-white text-sm px-4 py-3 rounded-full flex items-center gap-2">
          <i className="ri-checkbox-circle-fill text-primary-400"></i> {toast}
        </div>
      ) : null}
    </AppShell>
  );
}
